import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';

interface TimeHorizonOutlook {
  period: string;
  sentiment: 'very_bullish' | 'bullish' | 'neutral' | 'bearish' | 'very_bearish';
  confidence: number;
  rationale: string;
}

interface SentimentOutlookDiagramProps {
  outlooks: TimeHorizonOutlook[];
  topic: string;
  modelsCount: number;
}

const SENTIMENT_CONFIG: Record<string, { label: string; value: number; color: string; barClass: string; icon: React.ReactNode }> = {
  very_bullish: { label: 'Very Bullish', value: 100, color: 'text-emerald-600 dark:text-emerald-400', barClass: 'from-emerald-500 to-emerald-400', icon: <TrendingUp className="h-3.5 w-3.5" /> },
  bullish:      { label: 'Bullish',      value: 75,  color: 'text-emerald-500 dark:text-emerald-400', barClass: 'from-emerald-400 to-emerald-300', icon: <TrendingUp className="h-3.5 w-3.5" /> },
  neutral:      { label: 'Neutral',      value: 50,  color: 'text-muted-foreground',                 barClass: 'from-muted-foreground/60 to-muted-foreground/40', icon: <Minus className="h-3.5 w-3.5" /> },
  bearish:      { label: 'Bearish',      value: 25,  color: 'text-rose-500 dark:text-rose-400',       barClass: 'from-rose-400 to-rose-300', icon: <TrendingDown className="h-3.5 w-3.5" /> },
  very_bearish: { label: 'Very Bearish', value: 0,   color: 'text-rose-600 dark:text-rose-400',       barClass: 'from-rose-500 to-rose-400', icon: <TrendingDown className="h-3.5 w-3.5" /> },
};

const getThermometerColor = (value: number) => {
  if (value >= 80) return 'bg-emerald-500';
  if (value >= 60) return 'bg-emerald-400';
  if (value >= 40) return 'bg-amber-400';
  if (value >= 20) return 'bg-rose-400';
  return 'bg-rose-500';
};

const getThermometerBg = (value: number) => {
  if (value >= 80) return 'bg-emerald-500/10';
  if (value >= 60) return 'bg-emerald-400/10';
  if (value >= 40) return 'bg-amber-400/10';
  if (value >= 20) return 'bg-rose-400/10';
  return 'bg-rose-500/10';
};

export const SentimentOutlookDiagram = ({ outlooks, topic, modelsCount }: SentimentOutlookDiagramProps) => {
  if (!outlooks || outlooks.length === 0) return null;

  // Extract the likely asset name from the topic
  const assetName = topic.trim().length > 40 ? topic.trim().slice(0, 40) + '…' : topic.trim();

  // Scale markers for the thermometer
  const SCALE = [
    { label: 'Very Bullish', pct: 100 },
    { label: 'Bullish', pct: 75 },
    { label: 'Neutral', pct: 50 },
    { label: 'Bearish', pct: 25 },
    { label: 'Very Bearish', pct: 0 },
  ];

  const thermHeight = 200; // px for the thermometer bar

  return (
    <Card className="border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-transparent">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-violet-500/15 flex items-center justify-center">
            <TrendingUp className="h-4 w-4 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <CardTitle className="text-base">
              Price Direction Outlook — <span className="text-violet-600 dark:text-violet-400">{assetName}</span>
            </CardTitle>
            <CardDescription>
              Asset-specific sentiment projection · {modelsCount} models consensus · Triple-Loop verified (P1→P1B→P2)
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Thermometer Bars */}
        <div className="grid grid-cols-4 gap-3">
          {outlooks.map((o, i) => {
            const cfg = SENTIMENT_CONFIG[o.sentiment] || SENTIMENT_CONFIG.neutral;
            const fillPct = cfg.value;

            return (
              <div key={i} className="flex flex-col items-center">
                {/* Period label */}
                <span className="text-xs font-semibold text-foreground mb-2 text-center">{o.period}</span>

                {/* Thermometer container */}
                <div className="relative w-10 rounded-full bg-muted/40 border border-border overflow-hidden" style={{ height: thermHeight }}>
                  {/* Scale marks */}
                  {SCALE.map((s) => (
                    <div
                      key={s.pct}
                      className="absolute left-0 right-0 border-t border-border/40"
                      style={{ bottom: `${s.pct}%` }}
                    />
                  ))}

                  {/* Fill */}
                  <div
                    className={`absolute bottom-0 left-0 right-0 rounded-b-full transition-all duration-700 ease-out ${getThermometerColor(fillPct)}`}
                    style={{ height: `${fillPct}%` }}
                  />

                  {/* Level indicator line */}
                  <div
                    className="absolute left-0 right-0 h-0.5 bg-foreground/70 shadow-sm transition-all duration-700"
                    style={{ bottom: `${fillPct}%` }}
                  />
                </div>

                {/* Sentiment label */}
                <div className={`mt-2 flex items-center gap-1 ${cfg.color}`}>
                  {cfg.icon}
                </div>
                <Badge variant="outline" className={`mt-1 text-[9px] ${cfg.color} px-1.5`}>
                  {cfg.label}
                </Badge>

                {/* Confidence */}
                <span className="text-[10px] text-muted-foreground mt-1">
                  {o.confidence}% conf.
                </span>
              </div>
            );
          })}
        </div>

        {/* Scale legend */}
        <div className="flex items-center justify-center gap-4 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Bullish</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" /> Neutral</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" /> Bearish</span>
        </div>

        {/* Rationale cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {outlooks.map((o, i) => {
            const cfg = SENTIMENT_CONFIG[o.sentiment] || SENTIMENT_CONFIG.neutral;
            return (
              <div key={i} className={`rounded-lg border p-2.5 ${getThermometerBg(cfg.value)}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-foreground">{o.period}</span>
                  <Badge variant="outline" className={`text-[9px] ${cfg.color}`}>{cfg.label}</Badge>
                </div>
                <p className="text-[11px] text-muted-foreground leading-tight">{o.rationale}</p>
              </div>
            );
          })}
        </div>

        {/* Disclaimer */}
        <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-2.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[10px] text-muted-foreground">
            Projections are for <strong>{assetName}</strong> specifically, derived from cross-model consensus on current weak signals and catalysts.
            Triple-Loop verification applied (P1 Framing → P1B Challenge → P2 Evidence). Not financial advice.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
